/**
 * 微服务发现工具
 * 通过msLogin获取其他微服务的地址信息
 */

export interface ServiceInfo {
  serviceName: string;
  displayName: string;
  frontendUrl: string;
  backendUrl: string;
  status: 'online' | 'offline';
  healthStatus: boolean;
}

export interface ServiceDiscoveryConfig {
  msLoginUrl: string;
  timeout?: number;
  cacheTimeout?: number;
}

export class ServiceDiscovery {
  private config: ServiceDiscoveryConfig;
  private serviceCache: Map<string, { service: ServiceInfo; timestamp: number }> = new Map();
  private defaultTimeout: number = 5000;
  private defaultCacheTimeout: number = 60000; // 1分钟缓存

  constructor(config: ServiceDiscoveryConfig) {
    this.config = {
      timeout: config.timeout || this.defaultTimeout,
      cacheTimeout: config.cacheTimeout || this.defaultCacheTimeout,
      ...config
    };
  }

  /**
   * 获取所有微服务列表
   */
  async getAllServices(): Promise<ServiceInfo[]> {
    try {
      const response = await fetch(`${this.config.msLoginUrl}/api/microservices`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(this.config.timeout!)
      });

      if (!response.ok) {
        throw new Error(`获取微服务列表失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(`获取微服务列表失败: ${result.message || '未知错误'}`);
      }

      // 转换为标准格式
      return result.data.map((service: any) => ({
        serviceName: service.name || service.key,
        displayName: service.displayName || service.name,
        frontendUrl: service.url, // service_endpoint
        backendUrl: service.backendUrl, // service_url
        status: service.status === 'online' ? 'online' : 'offline',
        healthStatus: service.healthStatus || false
      }));

    } catch (error) {
      console.error('ServiceDiscovery: 获取微服务列表失败:', error);
      throw error;
    }
  }

  /**
   * 根据服务名获取特定微服务信息
   */
  async getService(serviceName: string): Promise<ServiceInfo | null> {
    // 检查缓存
    const cached = this.serviceCache.get(serviceName);
    if (cached && Date.now() - cached.timestamp < this.config.cacheTimeout!) {
      return cached.service;
    }

    try {
      const response = await fetch(`${this.config.msLoginUrl}/api/microservices/${serviceName}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(this.config.timeout!)
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`获取微服务信息失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(`获取微服务信息失败: ${result.message || '未知错误'}`);
      }

      const service: ServiceInfo = {
        serviceName: result.data.name || serviceName,
        displayName: result.data.displayName || result.data.name,
        frontendUrl: result.data.url,
        backendUrl: result.data.backendUrl,
        status: result.data.status === 'online' ? 'online' : 'offline',
        healthStatus: result.data.healthStatus || false
      };

      // 更新缓存
      this.serviceCache.set(serviceName, {
        service,
        timestamp: Date.now()
      });

      return service;

    } catch (error) {
      console.error(`ServiceDiscovery: 获取微服务 ${serviceName} 信息失败:`, error);
      throw error;
    }
  }

  /**
   * 获取微服务的后端API地址
   */
  async getServiceBackendUrl(serviceName: string): Promise<string | null> {
    const service = await this.getService(serviceName);
    return service?.backendUrl || null;
  }

  /**
   * 获取微服务的前端地址
   */
  async getServiceFrontendUrl(serviceName: string): Promise<string | null> {
    const service = await this.getService(serviceName);
    return service?.frontendUrl || null;
  }

  /**
   * 检查微服务是否在线
   */
  async isServiceOnline(serviceName: string): Promise<boolean> {
    const service = await this.getService(serviceName);
    return service?.healthStatus || false;
  }

  /**
   * 清除缓存
   */
  clearCache(): void {
    this.serviceCache.clear();
  }

  /**
   * 清除特定服务的缓存
   */
  clearServiceCache(serviceName: string): void {
    this.serviceCache.delete(serviceName);
  }
}

// 默认配置的服务发现实例
const defaultMsLoginUrl = process.env.REACT_APP_MSLOGIN_URL || 'http://localhost:50090';

export const serviceDiscovery = new ServiceDiscovery({
  msLoginUrl: defaultMsLoginUrl
});

// 便捷函数
export const getServiceBackendUrl = (serviceName: string) =>
  serviceDiscovery.getServiceBackendUrl(serviceName);

export const getServiceFrontendUrl = (serviceName: string) =>
  serviceDiscovery.getServiceFrontendUrl(serviceName);

export const isServiceOnline = (serviceName: string) =>
  serviceDiscovery.isServiceOnline(serviceName);
